import { IonLabel, IonList, IonRouterLink } from '@ionic/react';
import { Form, Formik } from 'formik';
import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useInitiateSignup, useLogout } from '../../api/auth.hooks';
import { SignupRequest } from '../../api/graphql/generated';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { useQueryParameters } from '../../util/router';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormField } from '../form/ion-form.field';
import { IonItem } from '../ionic/IonItem';
import { SaveStatusToast } from '../toast/toast.gql';
import { LoginWrapper } from './login-wrapper';
import './login.css';

interface Form {
  serialNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  orgName: string;
}

const Signup = (): ReactElement => {
  const logout = useLogout();
  const { builder } = useBeaconRouter();
  const { i18n } = useTranslation();
  const signInSchema = yup.object().shape({
    serialNumber: yup.string().required('*'),
    orgName: yup.string().max(30).required('*'),
    firstName: yup.string().max(20).required('*'),
    lastName: yup.string().max(20).required('*'),
    email: yup
      .string()
      .email(i18n.t('common:login_invalid_email'))
      .required('*'),
    confirmEmail: yup
      .string()
      .oneOf([yup.ref('email'), undefined], i18n.t('common:emails_must_match'))
      .required('*'),
  });

  const [signup, { loading, error }] = useInitiateSignup();

  const queryParams = useQueryParameters();
  const forceLogout = queryParams.get('forceLogout') || undefined;

  const onSubmit = async (form: Form) => {
    logger.info('initiating signup', {
      serialNumber: form.serialNumber,
      email: form.email,
    });

    await signup({
      variables: {
        input: {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          orgName: form.orgName,
          serialNumber: form.serialNumber,
        },
      },
    });
  };

  useEffect(() => {
    if (forceLogout) {
      logout();
    }
  }, [forceLogout, logout]);

  const initialValues: SignupRequest & { confirmEmail: string } = {
    serialNumber: '',
    orgName: '',
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
  };

  return (
    <LoginWrapper>
      <Formik
        validationSchema={signInSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnMount={true}
      >
        {({ isValid }) => {
          return (
            <>
              <Form>
                <IonList lines="none">
                  <IonItem>
                    <IonLabel>{i18n.t('common:login_get_started')}</IonLabel>
                  </IonItem>
                  <IonFormField
                    label={i18n.t('common:organization_name')}
                    name="orgName"
                    inputProps={{
                      type: 'text',
                    }}
                  />
                  <IonFormField
                    label={i18n.t('common:robot_serial_number')}
                    name="serialNumber"
                    fieldInfo={i18n.t('common:robot_serial_number_info')}
                    inputProps={{
                      type: 'text',
                      autofocus: true,
                    }}
                  />
                  <IonFormField
                    label={i18n.t('common:first_name')}
                    name="firstName"
                    inputProps={{
                      type: 'text',
                    }}
                  />
                  <IonFormField
                    label={i18n.t('common:last_name')}
                    name="lastName"
                    inputProps={{
                      type: 'text',
                    }}
                  />
                  <IonFormField
                    label={i18n.t('common:email')}
                    name="email"
                    inputProps={{
                      type: 'email',
                      autocomplete: 'email',
                    }}
                  />
                  <IonFormField
                    label={i18n.t('common:confirm_email')}
                    name="confirmEmail"
                    inputProps={{
                      type: 'email',
                      autocomplete: 'email',
                    }}
                  />
                </IonList>
                <br />
                <FormSubmitButton
                  loading={loading}
                  disabled={!isValid}
                  label={i18n.t('common:signup')}
                  placement="center"
                />
                <br />
                <div style={{ textAlign: 'center' }}>
                  <IonRouterLink routerLink={builder.getAuthLoginRoute()}>
                    {i18n.t('common:back_to_login')}
                  </IonRouterLink>
                </div>
              </Form>
              <SaveStatusToast
                error={error}
                loading={loading}
                defaultErrorMessageKey={'common:signup_failed'}
              />
            </>
          );
        }}
      </Formik>
    </LoginWrapper>
  );
};

export { Signup };
